import React, {useState, useCallback} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Container, Row, Col} from '@modifi/ui'
import {airBlue, semiDarkGray} from '@modifi/colors'
import {Trans} from '@modifi/plugin-i18n'

import {DefaultComponentWrapper} from '../common'
import {
  FAQWrapper,
  DisclosureWidget,
  QuestionText,
  StyledParagraph,
  Anchor,
  StyledHr,
  AnswerWrapper,
  QuestionRow,
} from './styles'

const componentMapping = {
  span: <span />,
  p: <StyledParagraph />,
  b: <b />,
  br: <br />,
  i: <i />,
  a: <Anchor target="_blank" rel="noopener noreferrer" />,
}

const faqItemPropTypes = {
  question: PropTypes.string.isRequired,
  answer: PropTypes.string.isRequired,
}

const faqItemDefaultProps = {}

export type FAQItemProps = InferPropTypes<typeof faqItemPropTypes, typeof faqItemDefaultProps>

const FAQItem: React.FC<FAQItemProps> = ({question, answer}) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleDisclosureWidgetClick = useCallback(() => setIsOpen(!isOpen), [setIsOpen, isOpen])
  return (
    <FAQWrapper>
      <QuestionRow
        alignItems="baseline"
        mt={16}
        mb={16}
        mdMb={isOpen ? 24 : 16}
        onClick={handleDisclosureWidgetClick}
      >
        <Col col={10}>
          <QuestionText isOpen={isOpen}>{question}</QuestionText>
        </Col>
        <Col col={2}>
          <DisclosureWidget expandItem={isOpen} color={isOpen ? airBlue : semiDarkGray} />
        </Col>
      </QuestionRow>
      <Row>
        <Col col={10} md={11}>
          {isOpen && (
            <AnswerWrapper>
              <StyledParagraph>
                <Trans defaults={answer} components={componentMapping} />
              </StyledParagraph>
            </AnswerWrapper>
          )}
          <StyledHr />
        </Col>
      </Row>
    </FAQWrapper>
  )
}

FAQItem.defaultProps = faqItemDefaultProps
FAQItem.propTypes = faqItemPropTypes

const faqsPropTypes = {
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ).isRequired,
  backgroundColor: PropTypes.string,
}
const faqsDefaultProps = {
  backgroundColor: undefined,
}

export type FAQsProps = InferPropTypes<typeof faqsPropTypes, typeof faqsDefaultProps>

export const FAQs: React.FC<FAQsProps> = ({faqs, backgroundColor}) => (
  <DefaultComponentWrapper backgroundColor={backgroundColor}>
    <Container>
      {faqs?.map((faq, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <FAQItem question={faq?.question!} answer={faq?.answer!} key={`faq-${index}`} />
      ))}
    </Container>
  </DefaultComponentWrapper>
)

FAQs.propTypes = faqsPropTypes

FAQs.defaultProps = faqsDefaultProps

export default FAQs
